import { scaleUtc } from 'd3-scale';
import { useMemo } from 'react';

import { DataByXValue } from '../../../hooks/useDataByXValue';
import { GeneratorOutput } from '../../../hooks/useGeneratorOutput';
import { useNumberScale } from '../../../hooks/useNumberScale';
import { DataKeyType } from '../../../utils/DataProps';
import { getExtent } from '../../../utils/getExtent';
import { isNumberCoercible } from '../../../utils/isNumberCoercible';
import { NumberCoercible } from '../../../utils/NumberCoercible';
import { NumberScale } from '../../../utils/NumberScale';
import { NumberScaleType } from '../../../utils/ScaleType';
import { TimeScale } from '../../../utils/TimeScale';

interface UseScalesParams<TDataKeyType extends DataKeyType, TPointType, TDatum> {
  /**
   * The chart data, indexed by x-value.
   */
  dataByXValue: DataByXValue<TDataKeyType, TPointType, TDatum>;
  /**
   * The generator output for the chart data.
   */
  generatorOutput: GeneratorOutput<TDataKeyType, TPointType, TDatum>;
  /**
   * The desired height to scale over (including any top/bottom margin).
   */
  height: number;
  /**
   * A margin used to reduce the ranges of the scale.
   */
  margin: {
    bottom: number;
    left: number;
    right: number;
    top: number;
  };
  /**
   * The desired width to scale over (including any left/right margin).
   */
  width: number;
  /**
   * The maximum value to use in the x domain. If omitted, this value is inferred from the data.
   */
  xDomainMax: number | undefined;
  /**
   * The minimum value to use in the x domain. If omitted, this value is inferred from the data.
   */
  xDomainMin: number | undefined;
  /**
   * The maximum value to use in the y domain. If omitted, this value is inferred from the data.
   */
  yDomainMax: number | undefined;
  /**
   * The minimum value to use in the y domain. If omitted, this value is inferred from the data.
   */
  yDomainMin: number | undefined;
  /**
   * Whether to round the start and end of the y domain via d3's scale.nice().
   */
  yDomainRounding: boolean;
  /**
   * The scale to use for the y-axis.
   */
  yScaleType: NumberScaleType;
}

interface UseScalesReturnType {
  /**
   * The maximum value in the x domain, or undefined if no maximum could be determined. This value
   * may not match the x-scale's domain if a valid value could not be determined, in which case a
   * fallback value is used for the x-scale instead.
   */
  xDomainMax: NumberCoercible | undefined;
  /**
   * The minimum value in the x domain, or undefined if no minimum could be determined. This value
   * may not match the x-scale's domain if a valid value could not be determined, in which case a
   * fallback value is used for the x-scale instead.
   */
  xDomainMin: NumberCoercible | undefined;
  /**
   * The resulting x-scale.
   */
  xScale: TimeScale;
  /**
   * The maximum value in the x domain, or undefined if no maximum could be determined. This value
   * may not match the y-scale's domain if a valid value could not be determined, in which case a
   * fallback value is used for the y-scale instead.
   */
  yDomainMax: number | undefined;
  /**
   * The minimum value in the y domain, or undefined if no minimum could be determined. This value
   * may not match the y-scale's domain if a valid value could not be determined, in which case a
   * fallback value is used for the y-scale instead.
   */
  yDomainMin: number | undefined;
  /**
   * The resulting y-scale.
   */
  yScale: NumberScale;
}

export const useScales = <TDataKeyType extends DataKeyType, TPointType, TDatum>({
  dataByXValue,
  generatorOutput,
  height,
  margin,
  width,
  xDomainMax: xDomainMaxProp,
  xDomainMin: xDomainMinProp,
  yDomainMax: yDomainMaxProp,
  yDomainMin: yDomainMinProp,
  yDomainRounding,
  yScaleType,
}: UseScalesParams<TDataKeyType, TPointType, TDatum>): UseScalesReturnType => {
  const [yRangeMin, yRangeMax] = useMemo(
    () => [height - margin.bottom, margin.top] as const,
    [height, margin.bottom, margin.top],
  );
  const xRange = useMemo(() => [margin.left, width - margin.right] as const, [margin.left, margin.right, width]);

  const [xDomainMin, xDomainMax] = useMemo(() => {
    return getExtent({
      data: dataByXValue.map(([k]) => k),
      valueGetter: (key) => key,
      min: xDomainMinProp,
      max: xDomainMaxProp,
    });
  }, [dataByXValue, xDomainMaxProp, xDomainMinProp]);

  const [yDomainMin, yDomainMax] = useMemo(() => {
    const overrideMax = isNumberCoercible(yDomainMaxProp) ? yDomainMaxProp : undefined;

    const min = isNumberCoercible(yDomainMinProp) ? yDomainMinProp : 0;

    const getMax = () => {
      if (overrideMax !== undefined) {
        return overrideMax;
      }

      if (generatorOutput.length === 0) {
        return undefined;
      }

      const computedMax = Math.max(
        ...generatorOutput
          .flat()
          .map(([, upper]) => upper)
          .filter(Number.isFinite as (v: unknown) => v is number),
      );

      // special case: if all values are zero and domain max was not given, use 1 as max
      if (computedMax === 0) {
        return 1;
      }

      return computedMax;
    };
    const max = getMax();

    return [min, max];
  }, [generatorOutput, yDomainMaxProp, yDomainMinProp]);

  const xScale = useMemo(
    () =>
      scaleUtc()
        .domain([xDomainMin ?? 0, xDomainMax ?? 0])
        .range(xRange),
    [xDomainMin, xDomainMax, xRange],
  );

  const yScale = useNumberScale({
    domainMin: yDomainMin,
    domainMax: yDomainMax ?? 0,
    rangeStart: yRangeMin,
    rangeEnd: yRangeMax,
    scaleType: yScaleType,
    domainRounding: yDomainRounding,
  });

  return {
    xDomainMax,
    xDomainMin,
    xScale,
    yDomainMax,
    yDomainMin,
    yScale,
  };
};
